import { useLayoutEffect, useRef } from 'react';

const MOVE_MIN_PX = 1;
const MOVE_MS = 500;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const catalogSectionMoveDy = (fromTop: number | undefined, toTop: number): number =>
  fromTop == null ? 0 : fromTop - toTop;

export const catalogMoveDelta = (
  from: { left: number; top: number } | undefined,
  to: { left: number; top: number }
): { dx: number; dy: number } => ({
  dx: from == null ? 0 : from.left - to.left,
  dy: from == null ? 0 : from.top - to.top,
});

/** Expand/collapse remasures only. A reorder uses those last boxes as the FLIP origin. */
export const catalogMoveShouldPlay = (prevOrderKey: string | null, orderKey: string): boolean =>
  prevOrderKey != null && prevOrderKey !== orderKey;

export const useCatalogSectionMove = (rootRef: { current: HTMLElement | null }, orderKey: string, layoutKey = '') => {
  const boxes = useRef(new Map<string, { left: number; top: number }>());
  const prevOrderKey = useRef<string | null>(null);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const playMove = catalogMoveShouldPlay(prevOrderKey.current, orderKey) && !prefersReducedMotion();
    prevOrderKey.current = orderKey;
    const moving: HTMLElement[] = [];
    const nextBoxes = new Map<string, { left: number; top: number }>();
    for (const node of root.querySelectorAll<HTMLElement>('[data-bb-catalog], [data-bb-catalog-badge]')) {
      const id = node.getAttribute('data-bb-catalog-badge') || node.getAttribute('data-bb-catalog');
      if (!id) continue;
      const box = { left: node.offsetLeft, top: node.offsetTop };
      const { dx, dy } = playMove ? catalogMoveDelta(boxes.current.get(id), box) : { dx: 0, dy: 0 };
      nextBoxes.set(id, box);
      const moved = Math.abs(dx) >= MOVE_MIN_PX || Math.abs(dy) >= MOVE_MIN_PX;
      node.classList.toggle('is-moving', moved);
      node.style.transition = 'none';
      node.style.transform = moved ? `translate(${dx}px, ${dy}px)` : '';
      if (moved) moving.push(node);
    }
    boxes.current = nextBoxes;
    if (!moving.length) return;
    void root.offsetHeight;
    const play = () => {
      for (const node of moving) {
        node.style.transition = '';
        node.style.transform = '';
      }
    };
    const frame = requestAnimationFrame(play);
    const finish = window.setTimeout(() => {
      for (const node of moving) node.classList.remove('is-moving');
    }, MOVE_MS);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(finish);
    };
  }, [orderKey, layoutKey, rootRef]);
};
